import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { queryClient, trpc, trpcErrorCode } from "../../app/trpc.js";
import { BellIcon, Button, ChevronRightIcon, EmptyState, focusRing, Notice, PageTitle, SkeletonList } from "../../ui/index.js";
import { cabinetPath, fmtAgo, useCabinet } from "../cabinet/index.js";
import { currentSubscription, needsInstall, pushSupported } from "./push.js";

/**
 * Уведомления: новые записи, переносы, отмены, опоздания, передачи диалога, «взял Marko», «бот не знал»,
 * отключение Instagram, месячная сводка. Прямо отсюда можно «взять диалог» и подтвердить запись.
 */
export function NotificationsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const cab = useCabinet();
  const list = useQuery(trpc.cabinet.notifications.list.queryOptions({ salonId: cab.salonId, limit: 100 }, { refetchInterval: 30_000 }));
  const [pushOff, setPushOff] = useState(false);
  useEffect(() => {
    if (needsInstall() || !pushSupported()) setPushOff(true);
    else void currentSubscription().then((s) => setPushOff(!s));
  }, []);

  const refresh = () => void queryClient.invalidateQueries(trpc.cabinet.notifications.pathFilter());
  const markRead = useMutation(trpc.cabinet.notifications.markRead.mutationOptions({ onSuccess: refresh }));
  const markAll = useMutation(trpc.cabinet.notifications.markAllRead.mutationOptions({ onSuccess: refresh }));
  const take = useMutation(trpc.inbox.takeOver.mutationOptions());
  const confirm = useMutation(trpc.calendar.bookings.confirm.mutationOptions({ onSuccess: refresh }));

  const open = (n: { id: string; url: string | null; read: boolean }) => {
    if (!n.read) markRead.mutate({ notificationId: n.id });
    if (n.url) navigate(cabinetPath(cab.base, n.url));
  };
  const now = new Date();

  return (
    <div className="lg:max-w-3xl">
      <PageTitle
        actions={
          <Button variant="ghost" size="sm" disabled={markAll.isPending} onClick={() => markAll.mutate({ salonId: cab.salonId })}>
            {t("notif.markAll")}
          </Button>
        }
      >
        {t("notif.title")}
      </PageTitle>
      {pushOff && (
        <Link
          to={`${cab.base}/settings/notifications`}
          className={`mb-3 flex items-center gap-3 rounded-field bg-amber-50 px-4 py-3 text-sm text-amber-900 hover:bg-amber-100 ${focusRing}`}
        >
          <BellIcon className="h-5 w-5 shrink-0" />
          <span className="flex-1">{needsInstall() ? t("notif.installBanner") : t("notif.enableBanner")}</span>
          <ChevronRightIcon className="h-5 w-5 shrink-0" />
        </Link>
      )}
      {list.isPending && <SkeletonList rows={3} />}
      {list.isError && <Notice kind="error">{t("common.error")}</Notice>}
      {list.data?.length === 0 && <EmptyState icon={<BellIcon />} title={t("notif.empty")} />}
      {(take.isError || confirm.isError) && (
        <Notice kind="error">
          {trpcErrorCode(take.error) === "conflict" ? t("notif.alreadyTaken") : confirm.isError ? t("notif.alreadyHandled") : t("common.error")}
        </Notice>
      )}
      <div className="space-y-2">
        {list.data?.map((n) => (
          <div
            key={n.id}
            className={`rounded-card border px-4 py-3 shadow-card ${n.read ? "border-line bg-white hover:border-line-strong" : "border-brand/40 bg-brand-soft hover:border-brand"}`}
          >
            <button type="button" className={`block w-full rounded-md text-left ${focusRing}`} onClick={() => open(n)}>
              <div className="flex items-start justify-between gap-2">
                <div className={`flex min-w-0 items-center gap-2 ${n.read ? "font-medium text-ink" : "font-semibold text-ink"}`}>
                  {!n.read && <span className="h-2 w-2 shrink-0 rounded-full bg-brand" />}
                  <span className="truncate">{n.title}</span>
                </div>
                <span className="shrink-0 text-xs text-ink-3">{fmtAgo(n.createdAt, now, cab.timezone, i18n.language)}</span>
              </div>
              <div className="mt-1 text-sm text-ink-2">{n.body}</div>
            </button>
            {n.type === "conversation.escalated" && n.conversationId && (
              <Button
                size="sm"
                className="mt-2"
                disabled={take.isPending}
                onClick={() => {
                  const conversationId = n.conversationId as string;
                  take.mutate(
                    { conversationId },
                    {
                      onSuccess: () => {
                        markRead.mutate({ notificationId: n.id });
                        navigate(`${cab.base}/conversations/${conversationId}`);
                      },
                    },
                  );
                }}
              >
                {t("conv.takeOver")}
              </Button>
            )}
            {n.type === "booking.pending" && n.bookingId && (
              <div className="mt-2 flex gap-2">
                <Button size="sm" disabled={confirm.isPending} onClick={() => confirm.mutate({ bookingId: n.bookingId as string })}>
                  {t("cal.confirm")}
                </Button>
                <Button variant="secondary" size="sm" onClick={() => open(n)}>
                  {t("notif.open")}
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
